import { useEffect, useState } from "react";
import {
  BellRing,
  CalendarCheck,
  Check,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  FileText,
  HeartPulse,
  IndianRupee,
  MessageCircle,
  ShieldCheck,
  Star,
  Users,
} from "lucide-react";
import { ConversationLoader } from "../OlliLogo";
import { firstName } from "./signupState";

/**
 * The right-hand panel of the auth screens (design: components/Showcase.tsx).
 *
 * It is a static product illustration, the way the design draws it: the
 * "product" slides (front desk, inbox, CRM, automations) are fixed marketing
 * pictures, not anyone's data, and are never read from or sent to the API.
 * The "live" slide on each signup step shows what the person has typed so far.
 *
 * Changed from the design (see the integration report): the backend sends the
 * signup code by email, not WhatsApp, and reset links last 1 hour, not 30 minutes.
 */

/* ---------- Shared bits ---------- */

function Glass({ children, className = "", delay = 0 }) {
  return (
    <div
      style={{ animationDelay: `${delay}ms` }}
      className={`animate-fade-in rounded-lg border border-paper/70 bg-paper/65 p-4 shadow-lift backdrop-blur-xl ${className}`}
    >
      {children}
    </div>
  );
}

function CardHead({ icon: Icon, title, meta }) {
  return (
    <div className="flex items-center gap-3 pb-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-paper shadow-soft">
        <Icon size={18} strokeWidth={1.75} />
      </span>
      <span className="min-w-0 flex-1 truncate text-small font-semibold">{title}</span>
      {meta}
    </div>
  );
}

export function OlliSymbol({ className = "h-6 w-6" }) {
  return (
    <svg viewBox="-8 -6 156 160" className={className} aria-hidden="true">
      <circle cx="102" cy="48" r="34" className="fill-sun" />
      <path d="M124 28 C128 16 134 8 142 2 C128 2 116 8 108 18 Z" className="fill-sun" />
      <circle cx="60" cy="90" r="50" fill="none" strokeWidth="12" className="stroke-ink" />
      <circle cx="60" cy="90" r="38" fill="none" strokeWidth="24" className="stroke-paper" />
      <path d="M22 108 C21 128 15 142 4 153 C23 154 39 147 52 135 Z" className="fill-paper" />
    </svg>
  );
}

/* ---------- Cards ---------- */

function ChatCard({ data, delay }) {
  const clinic = data?.orgName?.trim() || "your clinic";
  const place = data?.city?.trim() ? `, ${data.city.trim()}` : "";
  const doctor = data?.name?.trim() ? firstName(data.name) : "";
  return (
    <Glass delay={delay}>
      <div className="flex items-center gap-3 border-b border-line/70 pb-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-ink">
          <OlliSymbol className="h-5 w-5" />
        </span>
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-small font-semibold">{data?.orgName?.trim() || "Your clinic"}</span>
          <span className="text-caption text-muted">AI front desk · online</span>
        </div>
      </div>
      <div className="flex flex-col gap-2 pt-3">
        <div className="max-w-[85%] self-start rounded-md rounded-bl-xs bg-mist px-3 py-2 text-small">Can I book a check-up tomorrow?</div>
        <div key={`${clinic}${place}${doctor}`} className="max-w-[90%] animate-fade-in self-end rounded-md rounded-br-xs bg-sun px-3 py-2 text-small">
          Hi! I'm Olli at {clinic}
          {place}.{doctor ? ` Dr. ${doctor} is free at 11:30 AM.` : " 11:30 AM is free."} Book it?
          <span className="mt-0.5 flex items-center justify-end gap-1 text-caption text-ink/60">
            9:41 AM <CheckCheck size={14} strokeWidth={1.75} />
          </span>
        </div>
        <div className="flex items-center gap-2 pt-1 text-caption text-muted">
          <ConversationLoader size={20} tone="white" />
          Patient is typing…
        </div>
      </div>
    </Glass>
  );
}

const schedule = [
  { time: "9:00 AM", name: "Aarav Mehta", what: "Root canal", status: "Confirmed", tone: "text-success" },
  { time: "10:30 AM", name: "Priya Nair", what: "Cleaning", status: "Reminded", tone: "text-sky-deep" },
  { time: "12:00 PM", name: "Rohan Iyer", what: "Follow-up", status: "Booked by Olli", tone: "text-ink" },
];

function ScheduleCard({ delay }) {
  return (
    <Glass delay={delay}>
      <CardHead icon={CalendarCheck} title="Today" meta={<span className="text-caption text-muted">18 booked</span>} />
      <ul className="flex flex-col">
        {schedule.map((s, i) => (
          <li key={s.time} className={i ? "flex items-center gap-3 border-t border-line/70 py-2.5" : "flex items-center gap-3 py-2.5"}>
            <span className="w-11 text-small font-semibold tabular-nums">{s.time}</span>
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-small font-medium">{s.name}</span>
              <span className="truncate text-caption text-muted">{s.what}</span>
            </div>
            <span className={`text-caption font-semibold ${s.tone}`}>{s.status}</span>
          </li>
        ))}
      </ul>
    </Glass>
  );
}

function HandledCard({ delay }) {
  return (
    <div
      style={{ animationDelay: `${delay ?? 0}ms` }}
      className="flex animate-fade-in flex-col gap-4 rounded-lg bg-[image:var(--gradient-sun)] p-4 shadow-lift"
    >
      <div className="flex items-center justify-between">
        <span className="text-small font-semibold">Olli handled today</span>
        <MessageCircle size={18} strokeWidth={1.75} />
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-stat font-semibold tabular-nums">142</span>
        <span className="text-small">chats</span>
      </div>
      <div className="flex gap-4 text-caption font-medium">
        <span>
          <span className="tabular-nums">31</span> bookings
        </span>
        <span>
          <span className="tabular-nums">4</span> sent to staff
        </span>
      </div>
    </div>
  );
}

const flow = [
  { day: "Day 1", text: "How are you feeling after the procedure?", state: "Sent" },
  { day: "Day 3", text: "Reminder: take your medicines after meals", state: "Sent" },
  { day: "Day 7", text: "Book your follow-up visit", state: "Scheduled" },
];

function AutomationCard({ delay }) {
  return (
    <Glass delay={delay}>
      <CardHead icon={HeartPulse} title="After root canal" meta={<span className="rounded-full bg-sun px-2 py-0.5 text-caption font-semibold">On</span>} />
      <ol className="flex flex-col">
        {flow.map((f, i) => (
          <li key={f.day} className="flex gap-3">
            <div className="flex flex-col items-center">
              <span className={f.state === "Sent" ? "mt-1.5 h-2.5 w-2.5 rounded-full bg-ink" : "mt-1.5 h-2.5 w-2.5 rounded-full border-2 border-ink bg-paper"} />
              {i < flow.length - 1 && <span className="w-px flex-1 bg-line" />}
            </div>
            <div className="flex min-w-0 flex-1 flex-col pb-3">
              <span className="text-caption font-semibold">
                {f.day} <span className="font-normal text-muted">· {f.state}</span>
              </span>
              <span className="text-small text-ink/80">{f.text}</span>
            </div>
          </li>
        ))}
      </ol>
    </Glass>
  );
}

function PatientCard({ delay }) {
  return (
    <Glass delay={delay}>
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-sky-soft text-small font-semibold text-sky-deep">PN</span>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-small font-semibold">Priya Nair</span>
          <span className="text-caption text-muted">Patient since Mar 2024 · 6 visits</span>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2 border-t border-line/70 pt-3 text-caption">
        <Star size={14} strokeWidth={1.75} />
        <span className="font-medium">Left a 5-star review after her last visit</span>
      </div>
    </Glass>
  );
}

function ClinicCard({ data, delay }) {
  const rows = [
    { k: "Type", v: data.orgType },
    { k: "Address", v: [data.address, data.city].filter((x) => x.trim()).join(", ") },
    { k: "State", v: [data.state, data.pin].filter((x) => x.trim()).join(" · ") },
  ];
  return (
    <Glass delay={delay}>
      <div className="flex items-center gap-3 pb-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-ink text-small font-semibold text-sun">
          {(data.orgName.trim()[0] || "C").toUpperCase()}
        </span>
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-body font-semibold">{data.orgName.trim() || "Your clinic"}</span>
          <span className="truncate text-caption text-muted">{data.website.trim() || "Clinic profile"}</span>
        </div>
      </div>
      <dl className="flex flex-col">
        {rows.map((r) => (
          <div key={r.k} className="flex gap-3 border-t border-line/70 py-2">
            <dt className="w-16 shrink-0 text-caption text-muted">{r.k}</dt>
            <dd className={r.v ? "min-w-0 truncate text-caption font-medium" : "text-caption text-muted/70"}>{r.v || "Not added yet"}</dd>
          </div>
        ))}
      </dl>
    </Glass>
  );
}

function InvoiceCard({ data, delay }) {
  const legal = data.legalName.trim() || data.orgName.trim() || "Your organisation";
  return (
    <Glass delay={delay}>
      <CardHead icon={FileText} title="Tax invoice" meta={<span className="text-caption text-muted tabular-nums">OLLI-0001</span>} />
      <div className="flex flex-col gap-0.5 pb-3">
        <span className="text-body font-semibold">{legal}</span>
        <span className="text-caption text-muted">
          {[data.address, data.city, data.state, data.pin].filter((v) => v.trim()).join(", ") || "Address from your organisation details"}
        </span>
      </div>
      <dl className="grid grid-cols-2 gap-3 border-t border-line/70 pt-3">
        <div className="flex flex-col gap-0.5">
          <dt className="text-caption text-muted">GSTIN</dt>
          <dd className="text-small font-semibold tabular-nums">{data.hasGst ? data.gstin || "Not added" : "Not registered"}</dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt className="text-caption text-muted">PAN</dt>
          <dd className="text-small font-semibold tabular-nums">{data.pan || "Not added"}</dd>
        </div>
      </dl>
    </Glass>
  );
}

function NoteCard({ icon: Icon, title, body, delay }) {
  return (
    <Glass delay={delay}>
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-paper shadow-soft">
          <Icon size={18} strokeWidth={1.75} />
        </span>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-small font-semibold">{title}</span>
          <span className="text-caption text-muted">{body}</span>
        </div>
      </div>
    </Glass>
  );
}

/** The design shows a WhatsApp message with a sample code; the real code goes by email, and no code is ever shown here. */
function OtpCard({ email, delay }) {
  return (
    <Glass delay={delay}>
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ink">
          <OlliSymbol className="h-5 w-5" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex items-center justify-between gap-3">
            <span className="text-small font-semibold">Olli AI · Email</span>
            <span className="text-caption text-muted">now</span>
          </div>
          <span className="text-small">
            <span className="font-semibold tabular-nums">••••••</span> is your Olli verification code. It expires in 10 minutes.
          </span>
          <span className="truncate pt-1 text-caption text-muted tabular-nums">To {email || "your work email"}</span>
        </div>
      </div>
    </Glass>
  );
}

function RevenueCard({ delay }) {
  const bars = [40, 55, 48, 70, 62, 85, 78];
  return (
    <Glass delay={delay}>
      <CardHead icon={IndianRupee} title="This week" meta={<span className="text-caption font-semibold text-success">+12.4%</span>} />
      <div className="flex items-baseline gap-2 pb-3">
        <span className="text-h2 font-semibold tabular-nums">₹1,84,200</span>
      </div>
      <div className="flex h-16 items-end gap-1.5">
        {bars.map((h, i) => (
          <span key={i} style={{ height: `${h}%` }} className={i === bars.length - 2 ? "flex-1 rounded-xs bg-ink" : "flex-1 rounded-xs bg-sky"} />
        ))}
      </div>
    </Glass>
  );
}

/* ---------- Setup: the four products coming online ---------- */

export const setupModules = [
  { icon: MessageCircle, name: "WhatsApp inbox", task: "Setting up your WhatsApp inbox" },
  { icon: Users, name: "Patient CRM", task: "Building your patient CRM" },
  { icon: BellRing, name: "AI front desk", task: "Training your AI front desk" },
  { icon: HeartPulse, name: "Treatment automations", task: "Loading pre and post treatment automations" },
];

function ModulesShowcase({ done }) {
  return (
    <div className="grid w-full max-w-xl grid-cols-2 gap-4">
      {setupModules.map((m, i) => {
        const Icon = m.icon;
        const state = i < done ? "on" : i === done ? "active" : "off";
        return (
          <div
            key={m.name}
            style={{ animationDelay: `${i * 80}ms` }}
            className={
              state === "on"
                ? "flex h-40 animate-fade-in flex-col justify-between rounded-lg bg-[image:var(--gradient-sun)] p-5 shadow-lift"
                : "flex h-40 animate-fade-in flex-col justify-between rounded-lg border border-paper/70 bg-paper/55 p-5 shadow-soft backdrop-blur-xl"
            }
          >
            <div className="flex items-start justify-between">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-paper shadow-soft">
                <Icon size={20} strokeWidth={1.75} />
              </span>
              {state === "on" ? (
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-ink text-paper">
                  <Check size={16} strokeWidth={2.25} />
                </span>
              ) : state === "active" ? (
                <ConversationLoader size={28} tone="white" />
              ) : null}
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-body font-semibold">{m.name}</span>
              <span className={state === "on" ? "text-caption font-medium" : "text-caption text-muted"}>
                {state === "on" ? "Ready" : state === "active" ? "Setting up…" : "Waiting"}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------- Inbox card (product slide) ---------- */

const threads = [
  { initials: "KS", name: "Kavya Sharma", last: "Is parking available near the clinic?", time: "9:42 AM", unread: 2, by: "Olli replying" },
  { initials: "VM", name: "Vikram Menon", last: "Sent the X-ray report", time: "9:30 AM", unread: 0, by: "Assigned to Dr. Rao" },
  { initials: "SJ", name: "Sneha Joshi", last: "Thanks, see you Saturday!", time: "9:12 AM", unread: 0, by: "Resolved by Olli" },
];

function InboxCard({ delay }) {
  return (
    <Glass delay={delay}>
      <CardHead icon={MessageCircle} title="Inbox" meta={<span className="text-caption text-muted">3 open</span>} />
      <ul className="flex flex-col">
        {threads.map((t, i) => (
          <li key={t.name} className={i ? "flex items-center gap-3 border-t border-line/70 py-2.5" : "flex items-center gap-3 py-2.5"}>
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-soft text-caption font-semibold text-sky-deep">{t.initials}</span>
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="flex items-center justify-between gap-2">
                <span className="truncate text-small font-semibold">{t.name}</span>
                <span className="text-caption text-muted tabular-nums">{t.time}</span>
              </span>
              <span className="truncate text-caption text-muted">{t.last}</span>
              <span className="text-caption font-medium text-ink/70">{t.by}</span>
            </div>
            {t.unread > 0 && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-ink px-1.5 text-caption font-semibold text-paper tabular-nums">{t.unread}</span>
            )}
          </li>
        ))}
      </ul>
    </Glass>
  );
}

function VisitsCard({ delay }) {
  const visits = [
    { date: "12 Aug", what: "Root canal, sitting 2", fee: "₹4,500" },
    { date: "28 Jul", what: "Root canal, sitting 1", fee: "₹4,500" },
    { date: "02 Mar", what: "Cleaning", fee: "₹1,800" },
  ];
  return (
    <Glass delay={delay}>
      <CardHead icon={CalendarCheck} title="Visit history" />
      <ul className="flex flex-col">
        {visits.map((v, i) => (
          <li key={v.date} className={i ? "flex items-center gap-3 border-t border-line/70 py-2" : "flex items-center gap-3 py-2"}>
            <span className="w-12 text-caption font-semibold tabular-nums">{v.date}</span>
            <span className="min-w-0 flex-1 truncate text-small">{v.what}</span>
            <span className="text-small font-semibold tabular-nums">{v.fee}</span>
          </li>
        ))}
      </ul>
    </Glass>
  );
}

/* ---------- Slides ---------- */

function Columns({ left, right }) {
  return (
    <div className="grid w-full max-w-xl grid-cols-2 items-start gap-4">
      <div className="flex flex-col gap-4">{left}</div>
      <div className="flex flex-col gap-4 pt-12">{right}</div>
    </div>
  );
}

const productSlides = [
  {
    id: "front-desk",
    label: "AI front desk",
    title: "Olli answers every patient on WhatsApp",
    body: (
      <Columns
        left={
          <>
            <ChatCard />
            <HandledCard delay={80} />
          </>
        }
        right={<ScheduleCard delay={140} />}
      />
    ),
  },
  {
    id: "inbox",
    label: "WhatsApp inbox",
    title: "One inbox for your whole team",
    body: (
      <Columns
        left={<InboxCard />}
        right={<NoteCard icon={Users} title="Olli hands over when needed" body="Billing and clinical questions go to the right person on your team" delay={100} />}
      />
    ),
  },
  {
    id: "crm",
    label: "Patient CRM",
    title: "Every patient's story in one place",
    body: <Columns left={<PatientCard />} right={<VisitsCard delay={100} />} />,
  },
  {
    id: "automations",
    label: "Treatment automations",
    title: "Care continues after the visit",
    body: (
      <Columns
        left={<NoteCard icon={BellRing} title="Before the visit" body="Fasting and prep reminders go out 12 hours ahead" />}
        right={<AutomationCard delay={100} />}
      />
    ),
  },
];

function stepSlide(step, data) {
  switch (step) {
    case "basic":
      return {
        id: "live",
        label: "Your preview",
        title: "This is how Olli greets your patients",
        body: <Columns left={<ChatCard data={data} />} right={<AutomationCard delay={100} />} />,
      };
    case "organisation":
      return data
        ? {
            id: "live",
            label: "Your preview",
            title: "Patients meet your clinic by name",
            body: <Columns left={<ClinicCard data={data} />} right={<ChatCard data={data} delay={100} />} />,
          }
        : null;
    case "tax":
      return data
        ? {
            id: "live",
            label: "Your preview",
            title: "Invoices ready for GST",
            body: (
              <Columns
                left={
                  <>
                    <InvoiceCard data={data} />
                    <NoteCard icon={ShieldCheck} title="Claim GST input credit" body="Every Olli invoice carries these details" delay={80} />
                  </>
                }
                right={<RevenueCard delay={140} />}
              />
            ),
          }
        : null;
    case "verify":
      return {
        id: "live",
        label: "Your code",
        title: "Check your email",
        body: (
          <Columns
            left={
              <>
                <OtpCard email={data?.email ?? ""} />
                <NoteCard icon={ShieldCheck} title="Why email" body="We send your code there, so we check the address works" delay={80} />
              </>
            }
            right={<ChatCard data={data} delay={140} />}
          />
        ),
      };
    case "forgot":
      return {
        id: "safe",
        label: "Security",
        title: "Your patient data stays safe",
        body: (
          <Columns
            left={<NoteCard icon={ShieldCheck} title="One-time reset links" body="Each link works once and expires in 1 hour" />}
            right={<ChatCard delay={100} />}
          />
        ),
      };
    default:
      return null;
  }
}

const AUTOPLAY_MS = 6000;

function Carousel({ slides, resetKey }) {
  const [index, setIndex] = useState(0);

  // Typing on a step jumps back to its live preview.
  useEffect(() => {
    setIndex(0);
  }, [resetKey]);

  useEffect(() => {
    if (slides.length < 2) return undefined;
    const t = setTimeout(() => setIndex((i) => (i + 1) % slides.length), AUTOPLAY_MS);
    return () => clearTimeout(t);
  }, [index, slides.length]);

  const go = (d) => setIndex((i) => (i + d + slides.length) % slides.length);

  return (
    <div className="flex h-full w-full max-w-xl flex-col" aria-roledescription="carousel">
      <div className="grid flex-1 items-center">
        {slides.map((s, i) => (
          <div
            key={s.id}
            aria-hidden={i !== index}
            aria-roledescription="slide"
            aria-label={`${i + 1} of ${slides.length}: ${s.label}`}
            className={
              i === index
                ? "col-start-1 row-start-1 flex flex-col gap-8 opacity-100 transition-opacity duration-300 ease-out"
                : "pointer-events-none col-start-1 row-start-1 flex flex-col gap-8 opacity-0 transition-opacity duration-200 ease-out"
            }
          >
            <div className="flex flex-col gap-2">
              <span className="text-small font-semibold text-ink/60">{s.label}</span>
              <p className="text-h2 font-semibold">{s.title}</p>
            </div>
            {/* Remount the active slide so its cards fade in again. */}
            <div key={i === index ? `on-${index}` : "off"}>{s.body}</div>
          </div>
        ))}
      </div>

      {slides.length > 1 && (
        <div className="flex items-center justify-between pt-6">
          <div className="flex items-center gap-2">
            {slides.map((s, i) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Show ${s.label}`}
                aria-current={i === index}
                className="flex h-6 items-center"
              >
                {i === index ? (
                  <span className="h-2 w-8 overflow-hidden rounded-full bg-ink/25">
                    <span key={index} className="block h-full w-full origin-left animate-slide-progress rounded-full bg-ink" />
                  </span>
                ) : (
                  <span className="h-2 w-2 rounded-full bg-ink/25 hover:bg-ink/50" />
                )}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Previous slide"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-paper/70 bg-paper/60 backdrop-blur-xl hover:bg-paper"
            >
              <ChevronLeft size={18} strokeWidth={1.75} />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Next slide"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-ink text-paper hover:bg-ink/85"
            >
              <ChevronRight size={18} strokeWidth={1.75} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** step: "signin" | "forgot" | "basic" | "organisation" | "tax" | "verify" | "setup" */
export function Showcase({ step, data, done = 0 }) {
  if (step === "setup") {
    return (
      <div className="flex w-full max-w-xl flex-col gap-8">
        <p className="text-h2 font-semibold">{done >= setupModules.length ? "Olli is ready" : "Getting Olli ready"}</p>
        <ModulesShowcase done={done} />
      </div>
    );
  }
  const live = stepSlide(step, data);
  const slides = live ? [live, ...productSlides] : productSlides;
  // The password never goes into the reset key (it would only be used for comparison, but keep it out anyway).
  const { password: _pw, ...visible } = data ?? {};
  const resetKey = step === "forgot" || step === "signin" ? step : `${step}:${JSON.stringify(visible)}`;
  return <Carousel slides={slides} resetKey={resetKey} />;
}
